import React, { useRef, useEffect, useState } from 'react';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined,
  UnorderedListOutlined, OrderedListOutlined, LinkOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import T from './theme';
import DOMPurify from 'dompurify';
import { avecLibelleTraduit } from '../../i18n/libelles';

const TOOLS = avecLibelleTraduit([
  { cmd: 'bold', Icon: BoldOutlined },
  { cmd: 'italic', Icon: ItalicOutlined },
  { cmd: 'underline', Icon: UnderlineOutlined },
  { cmd: 'insertUnorderedList', Icon: UnorderedListOutlined },
  { cmd: 'insertOrderedList', Icon: OrderedListOutlined },
], 'messagerie:editeur.outils', { cle: 'cmd', champ: 'title' });

// True when contentEditable HTML has no visible text (ignores empty tags/<br>/&nbsp;).
function isBlank(html) {
  if (!html) return true;
  const txt = html
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .trim();
  return txt === '';
}

function ToolbarBtn({ Icon, title, onCmd, active }) {
  const [hov, setHov] = useState(false);
  const bg = active ? T.accentSoft : hov ? T.borderSoft : 'transparent';
  return (
    <button
      type="button" title={title} aria-pressed={!!active}
      onMouseDown={e => { e.preventDefault(); onCmd(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 30, height: 28, borderRadius: 6, cursor: 'pointer',
        border: active ? `1px solid ${T.accent}55` : '1px solid transparent',
        background: bg, color: active ? T.accent : T.text,
        fontWeight: active ? 700 : 400,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .1s, color .1s, border-color .1s',
      }}
    >
      <Icon />
    </button>
  );
}

export default function OssanEditor({
  value = '', onChange, placeholder = '', minHeight = 120, readOnly = false, autoFocus = false,
}) {
  const { t } = useTranslation('messagerie');
  const ref = useRef(null);
  const lastEmit = useRef(null); // dernière valeur émise par la saisie (anti caret-reset)
  const [empty, setEmpty] = useState(isBlank(value));
  const [active, setActive] = useState({});

  // Reflect the formatting active at the caret on the toolbar buttons.
  const refreshActive = () => {
    if (readOnly || document.activeElement !== ref.current) return;
    const st = {};
    for (const t of TOOLS) {
      try { st[t.cmd] = document.queryCommandState(t.cmd); } catch { st[t.cmd] = false; }
    }
    setActive(st);
  };

  // Keep the active state in sync while the caret/selection moves inside the editor.
  useEffect(() => {
    if (readOnly) return undefined;
    const handler = () => refreshActive();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external value into the DOM, sanitized (XSS), without clobbering the
  // caret while typing (skip when the change is our own emitted value).
  useEffect(() => {
    const el = ref.current;
    if (!el || readOnly) return;
    if (value === lastEmit.current) return;
    const clean = DOMPurify.sanitize(value || '', { ADD_ATTR: ['contenteditable'] });
    if (el.innerHTML !== clean) {
      el.innerHTML = clean;
      setEmpty(isBlank(clean));
    }
  }, [value, readOnly]);

  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);

  if (readOnly) {
    return (
      <div
        className="cb-msg-body"
        style={{ fontSize: 14, lineHeight: 1.6, color: T.text, fontFamily: T.font }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || '') }}
      />
    );
  }

  const emit = () => {
    const html = ref.current?.innerHTML || '';
    const out = isBlank(html) ? '' : html;
    lastEmit.current = out;
    setEmpty(isBlank(html));
    onChange?.(out);
  };

  const exec = cmd => { ref.current?.focus(); document.execCommand(cmd, false, null); emit(); refreshActive(); };
  const onPaste = e => {
    e.preventDefault();
    const html = e.clipboardData?.getData('text/html');
    const text = e.clipboardData?.getData('text/plain') || '';
    if (html) {
      document.execCommand('insertHTML', false, DOMPurify.sanitize(html));
    } else {
      document.execCommand('insertText', false, text);
    }
    emit();
  };
  const addLink = () => {
    const url = window.prompt(t('editeur.adresseLien'));
    if (!url) return;
    let ok = false;
    try {
      const proto = new URL(url, window.location.origin).protocol;
      ok = ['http:', 'https:', 'mailto:', 'tel:'].includes(proto);
    } catch { ok = false; }
    if (!ok) {
      window.alert(t('editeur.lienInvalide'));
      return;
    }
    ref.current?.focus();
    document.execCommand('createLink', false, url);
    emit();
  };

  return (
    <div style={{
      border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden',
      background: T.panel, fontFamily: T.font,
    }}>
      <div style={{
        display: 'flex', gap: 2, padding: 6,
        borderBottom: `1px solid ${T.borderSoft}`, background: T.contextBg,
      }}>
        {TOOLS.map(t => <ToolbarBtn key={t.cmd} Icon={t.Icon} title={t.title} active={active[t.cmd]} onCmd={() => exec(t.cmd)} />)}
        <ToolbarBtn Icon={LinkOutlined} title={t('editeur.insererLien')} onCmd={addLink} />
      </div>
      <div style={{ position: 'relative' }}>
        {empty && placeholder && (
          <div style={{
            position: 'absolute', top: 10, left: 12, color: T.textMut,
            pointerEvents: 'none', fontSize: 14,
          }}>
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onPaste={onPaste}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          style={{
            minHeight, padding: '10px 12px', outline: 'none',
            fontSize: 14, lineHeight: 1.6, color: T.text, overflowY: 'auto',
          }}
        />
      </div>
    </div>
  );
}
